/**
 * Tipos y etiquetas del generador de contratos. Copia de
 * server/src/services/contratos/tipos.ts: si cambia algo allí, cambia aquí.
 */

export type ClavePlantilla = 'explotacion' | 'gestion' | 'finalizacion';
/** Modelo de contrato que se firma, sin contar la finalización. */
export type ModeloContrato = Exclude<ClavePlantilla, 'finalizacion'>;
export type FormatoClausula = 'parrafo' | 'vineta' | 'numerada' | 'subtitulo' | 'tabla' | 'especial';
export type Especial = 'identificacion-titular' | 'identificacion-viviendas' | 'firmas';

export interface OpcionVariable {
  valor: string;
  etiqueta: string;
}

export interface VariableDef {
  clave: string;
  etiqueta: string;
  tipo: 'porcentaje' | 'numero' | 'texto' | 'opcion';
  opciones?: OpcionVariable[];
  desdeFicha?: 'modelo';
  desdeFecha?: 'temporada_inicial';
  porDefecto?: string | number;
  calculada?: string;
  ayuda?: string;
  soloVariante?: string;
}

export interface Variante {
  clave: string;
  nombre: string;
  titulo?: string;
  texto: string;
  cuando?: { variable: string; valor: string };
}

export interface Tabla {
  cabecera: string[] | null;
  filas: string[][];
}

export interface Clausula {
  id: string;
  formato: FormatoClausula;
  nivel?: 0 | 1;
  titulo?: string;
  texto?: string;
  tabla?: Tabla;
  especial?: Especial;
  config?: any;
  variantes?: Variante[];
  opcional?: boolean;
}

export interface Seccion {
  id: string;
  titulo: string;
  nivel: 1 | 2;
  clausulas: Clausula[];
}

export type EstadoPlantilla = 'borrador' | 'publicada' | 'archivada';

export interface EntradaHistorial {
  at: string;
  por: string;
  accion: string;
  detalle?: string;
}

export interface RevisionPreajuste {
  desdeVersion: number;
  hastaVersion: number;
  motivos: string[];
  at: string;
}

export type Segmento = { tipo: 'igual' | 'anadido' | 'eliminado'; texto: string };

export interface CambioClausula {
  id: string;
  estado: 'igual' | 'anadida' | 'eliminada' | 'modificada';
  seccionId: string;
  seccionAntes?: string;
  titulo?: string;
  cambios: string[];
  antes?: Clausula;
  despues?: Clausula;
  segmentosTexto?: Segmento[];
  segmentosTitulo?: Segmento[];
}

export interface CambioSeccion {
  id: string;
  titulo: string;
  estado: 'igual' | 'anadida' | 'eliminada' | 'renombrada';
  tituloAntes?: string;
  clausulas: CambioClausula[];
}

export interface DiffPlantilla {
  de: number;
  a: number;
  secciones: CambioSeccion[];
  variables: { anadidas: string[]; eliminadas: string[]; cambiadas: string[] };
  resumen: { anadidas: number; eliminadas: number; modificadas: number; movidas: number; secciones: number };
}

export const ACCION_HISTORIAL: Record<string, string> = {
  creado: 'Creado',
  guardado: 'Guardado',
  generado: 'PDF generado',
  duplicado: 'Duplicado',
  anulado: 'Anulado',
  estado: 'Estado cambiado a mano',
  preajuste_guardado: 'Guardado como preajuste',
  enviado_firma: 'Enviado a firma',
  reenviado_firma: 'Vuelto a enviar a firma',
  firma: 'Firma',
  borrador: 'Borrador abierto',
  publicada: 'Publicada',
  archivada: 'Archivada',
};

export function describirResumenDiff(r: DiffPlantilla['resumen']): string {
  const partes: string[] = [];
  if (r.modificadas) partes.push(`${r.modificadas} modificada${r.modificadas === 1 ? '' : 's'}`);
  if (r.anadidas) partes.push(`${r.anadidas} añadida${r.anadidas === 1 ? '' : 's'}`);
  if (r.eliminadas) partes.push(`${r.eliminadas} quitada${r.eliminadas === 1 ? '' : 's'}`);
  if (r.movidas) partes.push(`${r.movidas} movida${r.movidas === 1 ? '' : 's'}`);
  if (r.secciones) partes.push(`${r.secciones} secci${r.secciones === 1 ? 'ón' : 'ones'} con cambios`);
  return partes.join(' · ') || 'sin cambios';
}

export interface PlantillaVersion {
  id: string;
  templateKey: ClavePlantilla;
  version: number;
  estado: EstadoPlantilla;
  nombre: string;
  titulo: string;
  variables: VariableDef[];
  secciones: Seccion[];
  notas?: string;
  historial?: EntradaHistorial[];
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  publicadaAt?: string;
  publicadaPor?: string;
  /** Solo en la respuesta de publicar. */
  publicacion?: { preajustesMarcados: number; cambios: string | null };
}

export interface ResumenPlantilla {
  id: string;
  templateKey: ClavePlantilla;
  version: number;
  estado: EstadoPlantilla;
  nombre: string;
  titulo: string;
  notas?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  publicadaAt?: string;
  publicadaPor?: string;
  secciones: number;
  clausulas: number;
}

export interface ModeloPlantilla {
  templateKey: ClavePlantilla;
  nombre: string;
  versionPublicada: number | null;
  versiones: ResumenPlantilla[];
}

export interface Sobrescritura {
  titulo?: string;
  texto?: string;
  tabla?: Tabla;
  eliminada?: boolean;
}

export interface ClausulaExtra {
  id: string;
  seccionId: string;
  despuesDe?: string | null;
  clausula: Clausula;
}

export interface Condiciones {
  valores: Record<string, string | number>;
  variantes: Record<string, string>;
  sobrescrituras: Record<string, Sobrescritura>;
  clausulasExtra: ClausulaExtra[];
}

export const CONDICIONES_VACIAS: Condiciones = { valores: {}, variantes: {}, sobrescrituras: {}, clausulasExtra: [] };

export interface Preajuste extends Condiciones {
  id: string;
  templateKey: ClavePlantilla;
  nombre: string;
  descripcion?: string;
  templateVersion: number;
  revisar?: RevisionPreajuste | null;
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
}

export interface Titular {
  nombre: string;
  dni: string;
  domicilio: string;
  iban: string;
  email: string;
}

export interface Vivienda {
  id?: string;
  nombre?: string;
  direccion: string;
  codigo: string;
  catastro?: string;
  nra?: string;
  garaje?: string;
}

export type TipoParte = 'owner' | 'lead';

export interface DatosParte {
  tipo: TipoParte;
  id: string;
  nombre: string;
  email?: string;
  telefono?: string;
  modeloFicha?: string | null;
  titulares: Titular[];
  viviendas: Vivienda[];
}

export interface RespuestaParte {
  datos: DatosParte;
  viviendasDisponibles: Vivienda[];
  modeloSugerido: ModeloContrato;
}

export type EstadoContrato = 'borrador' | 'generado' | 'enviado_firma' | 'firmado' | 'anulado';

export interface CampoFormulario {
  nombre: string;
  etiqueta: string;
  pagina: number;
  obligatorio: boolean;
  titular?: 1 | 2;
}

export interface PosicionFirma {
  rol: 'sereno' | 'propiedad_1' | 'propiedad_2';
  pagina: number;
  xMm: number;
  yMm: number;
  anchoMm: number;
  altoMm: number;
}

export interface PdfGenerado {
  blobPath: string;
  documentId?: string | null;
  generadoAt: string;
  paginas: number;
  bytes: number;
  campos: CampoFormulario[];
  firmas: PosicionFirma[];
}

export type RolFirmante = PosicionFirma['rol'];

export interface FirmanteContrato {
  rol: RolFirmante;
  nombre: string;
  apellidos: string;
  email?: string;
  telefono?: string;
  dni?: string;
  signatoryId?: string | null;
  estado?: string | null;
  estadoAt?: string | null;
  /** Recordatorios que Click & Sign le tiene programados o le ha mandado (al sincronizar). */
  recordatorios?: RecordatorioFirmante[];
}

export interface RecordatorioFirmante {
  canal: 'email' | 'sms';
  programadoAt: string;
  /** delivered, unknown (no ha salido), failed… */
  estadoCs: string;
  estadoAt: string | null;
}

/**
 * Click & Sign programa los recordatorios para todos al empezar y solo manda
 * los de quien no ha firmado; los que no salen se quedan en `unknown`.
 */
export function estadoRecordatorio(r: RecordatorioFirmante, firmanteEstado: string | null | undefined, ahora = Date.now()): 'enviado' | 'programado' | 'no_hizo_falta' | 'sin_confirmar' | 'fallido' {
  if (r.estadoCs === 'delivered') return 'enviado';
  if (r.estadoCs !== 'unknown') return 'fallido';
  if (firmanteEstado && firmanteEstado !== 'ready' && firmanteEstado !== 'new') return 'no_hizo_falta';
  return new Date(r.programadoAt).getTime() > ahora ? 'programado' : 'sin_confirmar';
}

export interface FirmaContrato {
  configId: number;
  configNombre?: string | null;
  signatureId: string;
  enviadoAt: string;
  enviadoPor?: string | null;
  estadoCs: string;
  estadoCsAt?: string | null;
  firmantes: FirmanteContrato[];
  pdfFirmado?: { blobPath: string; documentId?: string | null; guardadoAt: string } | null;
  sincronizadoAt?: string | null;
  /** Calendario de recordatorios de la configuración al enviar; los manda Click & Sign. */
  recordatorios?: RecordatoriosFirma | null;
}

export interface RecordatorioCs {
  canal: 'email' | 'sms';
  /** Horas desde el inicio de la firma. */
  horas: number;
  asunto?: string | null;
}

export interface RecordatoriosFirma {
  expiraHoras: number | null;
  recordatorios: RecordatorioCs[];
}

export interface RecordatoriosConfig extends RecordatoriosFirma {
  configId: number;
  nombre: string | null;
}

/** «3 días», «36 h»: los lapsos de Click & Sign van en horas. */
export function lapsoLegible(horas: number): string {
  if (horas % 24 === 0) return `${horas / 24} ${horas === 24 ? 'día' : 'días'}`;
  return `${Math.round(horas * 10) / 10} h`;
}

/** Fecha de cada recordatorio y de la caducidad contando desde el envío. */
export function calendarioRecordatorios(enviadoAt: string, r: RecordatoriosFirma): { recordatorios: Array<RecordatorioCs & { at: string }>; expiraAt: string | null } {
  const inicio = new Date(enviadoAt).getTime();
  const en = (horas: number) => new Date(inicio + horas * 3600_000).toISOString();
  return {
    recordatorios: r.recordatorios.map(x => ({ ...x, at: en(x.horas) })),
    expiraAt: r.expiraHoras ? en(r.expiraHoras) : null,
  };
}

export interface AjustesFirma {
  configPorModelo: Record<ClavePlantilla, number | null>;
  configId?: number | null;
  configNombre?: string | null;
  serenoFirma: boolean;
  sereno: { nombre: string; apellidos: string; email: string; telefono: string; dni?: string };
}

export interface FirmanteEntrada {
  rol: RolFirmante;
  nombre: string;
  apellidos: string;
  email?: string;
  telefono?: string;
  dni?: string;
}

export interface ConfigFirma {
  configId: number;
  nombre: string;
  estado: string | null;
}

export interface PreparacionFirma {
  ajustes: AjustesFirma;
  firmantes: FirmanteEntrada[];
  configs: ConfigFirma[] | null;
  errorConfigs: string | null;
}

export const ROL_FIRMANTE: Record<RolFirmante, string> = {
  sereno: 'Sereno',
  propiedad_1: 'Titular',
  propiedad_2: 'Segundo titular',
};

/** Estados que devuelve Click & Sign, con la misma etiqueta que la pestaña Firmas. */
export const ESTADO_CS: Record<string, { label: string; className: string }> = {
  new: { label: 'Nuevo', className: 'bg-gray-100 text-gray-600' },
  ready: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-700' },
  started: { label: 'Iniciado', className: 'bg-blue-100 text-blue-700' },
  delivered: { label: 'Entregado', className: 'bg-sereno-blue text-sereno-dark' },
  signed: { label: 'Firmado', className: 'bg-green-100 text-green-700' },
  declined: { label: 'Rechazado', className: 'bg-red-100 text-red-700' },
  expired: { label: 'Expirado', className: 'bg-gray-100 text-gray-500' },
  cancelled: { label: 'Cancelado', className: 'bg-gray-100 text-gray-400' },
};

export const ESTADOS_CS_REENVIABLES = ['declined', 'expired', 'cancelled'];

/** Enlace de solo lectura enviado a la parte, sin Click & Sign. */
export interface ConsultaContrato {
  id: string;
  token: string;
  email: string;
  nombre?: string | null;
  creadaAt: string;
  creadaPor?: string | null;
  expiraAt: string;
  revocadaAt?: string | null;
  revocadaPor?: string | null;
  correo: { estado: 'enviado' | 'error' | 'no_configurado' | 'no_enviado'; detalle?: string };
  /** Solo viaja al pedir el contrato entero, nunca en el listado. */
  documento?: DocumentoResuelto | null;
  vistas: number;
  descargas: number;
  primeraVistaAt?: string | null;
  ultimaVistaAt?: string | null;
}

export interface PreparacionConsulta {
  email: string;
  nombre: string;
  telefono: string;
}

export interface Contrato extends Condiciones {
  id: string;
  codigo: string;
  templateKey: ClavePlantilla;
  templateVersion: number;
  preajusteId?: string | null;
  preajusteNombre?: string | null;
  parte: { tipo: TipoParte; id: string; nombre: string };
  ownerId?: string | null;
  leadId?: string | null;
  propiedadIds: string[];
  viviendasEnBlanco: number;
  segundoTitular: boolean;
  datos?: DatosParte | null;
  estado: EstadoContrato;
  pdf?: PdfGenerado | null;
  resuelto?: DocumentoResuelto | null;
  signatureId?: string | null;
  firma?: FirmaContrato | null;
  origenId?: string | null;
  consultas?: ConsultaContrato[];
  notas?: string;
  historial?: EntradaHistorial[];
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  generadoAt?: string | null;
  generadoPor?: string | null;
}

/** Fila del listado: sin el documento resuelto, los datos congelados ni el historial. */
export type ContratoLigero = Omit<Contrato, 'resuelto' | 'datos' | 'historial'> & { tieneResuelto: boolean; viviendas: number | null; entradasHistorial: number };

export interface FilaCampo {
  etiqueta: string;
  valor?: string;
  campo?: string;
  nota?: string;
  obligatorio?: boolean;
  titular?: 1 | 2;
}

export type BloqueResuelto =
  | { tipo: 'titulo'; nivel: 1 | 2; texto: string; seccionId: string }
  | { tipo: 'parrafo'; clausulaId: string; titulo?: string; texto: string; modificada?: boolean }
  | { tipo: 'item'; clausulaId: string; estilo: 'vineta' | 'numerada'; nivel: 0 | 1; numero?: number; marca?: string; titulo?: string; texto: string; modificada?: boolean }
  | { tipo: 'subtitulo'; clausulaId: string; texto: string; modificada?: boolean }
  | { tipo: 'tabla'; clausulaId: string; tabla: Tabla; modificada?: boolean }
  | { tipo: 'campos'; clausulaId: string; titulo?: string; filas: FilaCampo[] }
  | { tipo: 'firmas'; clausulaId: string; firmantes: { rol: PosicionFirma['rol']; etiqueta: string }[]; lugarFecha: FilaCampo[] };

export interface Diferencia {
  clausulaId: string;
  titulo?: string;
  tipo: 'variante' | 'sobrescrita' | 'eliminada' | 'extra';
  detalle?: string;
}

export interface DocumentoResuelto {
  titulo: string;
  codigo: string;
  fecha: string;
  templateKey: ClavePlantilla;
  templateVersion: number;
  bloques: BloqueResuelto[];
  diferencias: Diferencia[];
  variables: Record<string, string>;
  variablesSinValor: string[];
}

// ─── Etiquetas ──────────────────────────────────────────────────────────────

export const NOMBRE_MODELO: Record<ClavePlantilla, string> = {
  explotacion: 'Cesión de explotación',
  gestion: 'Gestión (colaboración)',
  finalizacion: 'Finalización de contrato',
};

export const DESCRIPCION_MODELO: Record<ClavePlantilla, string> = {
  explotacion: 'Sereno explota la vivienda y liquida una renta variable al propietario. También para LCM.',
  gestion: 'Sereno intermedia y cobra una comisión por gestión de reservas.',
  finalizacion: 'Pone fin a un contrato de gestión o de explotación: fecha de efectos, reservas pendientes, liquidación final y retirada de dispositivos.',
};

/**
 * Primer día de la temporada de un año: una semana antes del Domingo de Ramos,
 * 14 días antes de Pascua. Copia de server/src/services/contratos/temporada.ts.
 */
export function inicioTemporada(anio: number): string {
  const a = anio % 19, b = Math.floor(anio / 100), c = anio % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const l = (32 + 2 * e + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(anio, mes - 1, dia, 12) - 14 * 86_400_000).toISOString().slice(0, 10);
}

const diaMadrid = (fecha?: string | null) => (fecha ? new Date(fecha) : new Date()).toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

/** Año de la primera temporada que aún no ha empezado en esa fecha (hoy si no se da). */
export function temporadaInicial(fecha?: string | null): number {
  const dia = diaMadrid(fecha);
  const anio = Number(dia.slice(0, 4));
  return dia <= inicioTemporada(anio) ? anio : anio + 1;
}

/** Si hoy ya ha empezado la temporada de ese año. */
export function temporadaEmpezada(anio: number): boolean {
  return diaMadrid() > inicioTemporada(anio);
}

/**
 * Valor efectivo de una variable antes de que lo toque el contrato: el del
 * preajuste, el de la ficha o la fecha si la variable sale de ellas, o el de la
 * plantilla. Es lo mismo que hace el servidor al resolver; `fecha` es la de
 * generación, o hoy mientras el contrato es un borrador.
 */
export function valorBase(v: VariableDef, preajuste: { valores?: Record<string, string | number> } | null, modeloFicha: ModeloContrato | null, fecha?: string | null): string | number | undefined {
  const delPreajuste = preajuste?.valores?.[v.clave];
  if (delPreajuste !== undefined && String(delPreajuste).trim() !== '') return delPreajuste;
  if (v.desdeFicha === 'modelo' && modeloFicha && (!v.opciones || v.opciones.some(o => o.valor === modeloFicha))) return modeloFicha;
  if (v.desdeFecha === 'temporada_inicial') return String(temporadaInicial(fecha));
  return v.porDefecto;
}

/** Variante que se aplica sola por el valor de una variable, si alguna cumple su condición. */
export function varianteAutomatica(c: Pick<Clausula, 'variantes'>, valores: Record<string, string | number | undefined>): Variante | null {
  return (c.variantes || []).find(x => x.cuando && String(valores[x.cuando.variable] ?? '') === x.cuando.valor) || null;
}

export const ESTADO_CONTRATO: Record<EstadoContrato, { label: string; className: string }> = {
  borrador: { label: 'Borrador', className: 'bg-yellow-100 text-yellow-700' },
  generado: { label: 'Generado', className: 'bg-green-100 text-green-700' },
  enviado_firma: { label: 'Enviado a firma', className: 'bg-blue-100 text-blue-700' },
  firmado: { label: 'Firmado', className: 'bg-emerald-100 text-emerald-800' },
  anulado: { label: 'Anulado', className: 'bg-gray-100 text-gray-500' },
};

export const ESTADO_PLANTILLA: Record<EstadoPlantilla, { label: string; className: string }> = {
  borrador: { label: 'Borrador', className: 'bg-yellow-100 text-yellow-700' },
  publicada: { label: 'Publicada', className: 'bg-green-100 text-green-700' },
  archivada: { label: 'Archivada', className: 'bg-gray-100 text-gray-500' },
};

export const FORMATO_CLAUSULA: Record<FormatoClausula, string> = {
  parrafo: 'Párrafo',
  vineta: 'Viñeta',
  numerada: 'Punto numerado',
  subtitulo: 'Subtítulo',
  tabla: 'Tabla',
  especial: 'Bloque de datos',
};

export const TIPO_DIFERENCIA: Record<Diferencia['tipo'], string> = {
  variante: 'Variante elegida',
  sobrescrita: 'Texto modificado',
  eliminada: 'Cláusula quitada',
  extra: 'Cláusula añadida',
};

/** Texto de una cláusula tal y como se enseña en el editor: título y cuerpo. */
export function textoClausula(c: Pick<Clausula, 'titulo' | 'texto'>): string {
  return [c.titulo, c.texto].filter(Boolean).join(': ');
}

/** Id corto y legible para cláusulas creadas desde el CRM. */
export function idNuevaClausula(texto: string): string {
  const base = texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `${base || 'clausula'}-${Math.random().toString(36).slice(2, 6)}`;
}

// ─── Procesar contrato firmado ──────────────────────────────────────────────
// Copia de server/src/services/contratos/procesar.ts.

export type CampoVivienda = 'codigo' | 'catastro' | 'nra' | 'garaje';

export interface FilaProcesado {
  clave: string;
  grupo: 'titular' | 'segundo' | 'condiciones';
  etiqueta: string;
  actual: string;
  nuevo: string;
  estado: 'nuevo' | 'distinto' | 'igual';
  marcar: boolean;
  aviso?: string;
}

export interface ViviendaProcesado {
  numero: number;
  direccion: string;
  propertyId: string | null;
  datos: Partial<Record<CampoVivienda, string>>;
}

export interface PropuestaProcesado {
  signatureId: string;
  ownerId: string;
  codigo: string;
  origen: 'generador' | 'word';
  filas: FilaProcesado[];
  viviendas: ViviendaProcesado[];
  propiedades: Array<{ id: string; nombre: string; direccion: string; actuales: Record<CampoVivienda, string> }>;
  archivarPdf: boolean;
}

export interface ResultadoProcesado {
  guardados: string[];
  viviendasActualizadas: number;
  pdfArchivado: boolean;
}

export const ETIQUETA_VIVIENDA: Record<CampoVivienda, string> = {
  codigo: 'Código de inscripción',
  catastro: 'Referencia catastral',
  nra: 'NRA',
  garaje: 'Plaza de garaje',
};
